import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { Response } from '../domain/response/response';

export interface GeometryResult {
  id?: string;
  island: string;
  name: string;
  color?: string;
  description?: string;
}

export interface ApiGeometry extends GeometryResult {
  geoJson: string;
}

export interface Geometry extends GeometryResult {
  geoJson: GeoJSON.GeoJSON;
}

@Injectable()
export class GeometryService {
  http = inject(HttpClient);

  getIslandGeometries(islandId: string, includeGeoJson?: boolean) {
    let url = '/api/geometry/';
    const params = new URLSearchParams();

    params.set('island', islandId);

    if (includeGeoJson) {
      params.set('includeGeojson', '1');
    }

    if (params.size) {
      url += '?' + params.toString();
    }

    if (includeGeoJson) {
      return this.http.get<ApiGeometry[]>(url).pipe(
        map<ApiGeometry[], Geometry[]>((g) =>
          g.map((g) => ({
            ...g,
            geoJson: JSON.parse(g.geoJson),
          }))
        )
      );
    } else {
      return this.http.get<GeometryResult[]>(url);
    }
  }

  getGeometry(id: string) {
    return this.http.get<ApiGeometry>(`/api/geometry/${id}`).pipe(
      map<ApiGeometry, Geometry>((g) => ({
        ...g,
        geoJson: JSON.parse(g.geoJson),
      }))
    );
  }

  deleteGeometry(id: string) {
    return this.http.delete(`/api/geometry/${id}`);
  }

  getGeojson(id: string) {
    return this.http
      .get<ApiGeometry>(`/api/geometry/${id}`, {
        headers: {
          Accept: 'application/geo+json',
        },
      })
      .pipe(map<ApiGeometry, string>((g) => g.geoJson));
  }

  saveGeometry(geometry: Geometry) {
    const apiGeometry: ApiGeometry = {
      ...geometry,
      geoJson: JSON.stringify(geometry.geoJson),
    };
    return this.http
      .post<Response<ApiGeometry>>('/api/geometry/', apiGeometry)
      .toPromise();
  }
}
